<template>
  <div>
    <assistant_teacher_bar />
    <v-container fluid class="pa-4 pa-md-8">
      <div class="d-flex flex-wrap align-center justify-space-between mb-6">
        <div>
          <h2 class="font-weight-bold primary--text mb-1">
            <v-icon large color="primary" class="mr-2">mdi-clipboard-alert-outline</v-icon>
            ตรวจสอบและติดตามข้อมูลครูผู้ช่วย
          </h2>
          <span class="grey--text text--darken-1 ml-2">
            สถิติการรายงานตัว ความผิดปกติของข้อมูล และส่งออก Excel
          </span>
        </div>
        <v-btn color="primary" :loading="loading" @click="loadAll">
          <v-icon left>mdi-refresh</v-icon>
          โหลดข้อมูลใหม่
        </v-btn>
      </div>

      <!-- ===== สรุปสถิติ ===== -->
      <v-row class="mb-4">
        <v-col cols="12" sm="6" md="3">
          <v-card outlined rounded="lg" class="pa-3 text-center">
            <v-icon color="primary" size="28">mdi-account-group</v-icon>
            <div class="text-h5 font-weight-bold mt-1">{{ formatNum(summary.total_assistant_teachers) }}</div>
            <div class="caption grey--text">ครูผู้ช่วยทั้งหมด (เลขบัตรไม่ซ้ำ)</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined rounded="lg" class="pa-3 text-center">
            <v-icon color="success" size="28">mdi-check-decagram</v-icon>
            <div class="text-h5 font-weight-bold mt-1">{{ formatNum(statusCount('receipt')) }}</div>
            <div class="caption grey--text">รับรายงานตัวเรียบร้อย</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined rounded="lg" class="pa-3 text-center">
            <v-icon color="warning" size="28">mdi-clock-alert-outline</v-icon>
            <div class="text-h5 font-weight-bold mt-1">{{ formatNum(statusCount('send') + statusCount('pending')) }}</div>
            <div class="caption grey--text">ยังไม่รายงานตัว / รอดำเนินการ</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined rounded="lg" class="pa-3 text-center">
            <v-icon color="error" size="28">mdi-file-document-alert-outline</v-icon>
            <div class="text-h5 font-weight-bold mt-1">{{ formatNum(summary.reported_no_evaluation_yet) }}</div>
            <div class="caption grey--text">รายงานตัวแล้วแต่ยังไม่มีผลประเมินเลย</div>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mb-6">
        <v-col cols="12" md="6">
          <v-card outlined rounded="lg" class="pa-3">
            <div class="font-weight-bold mb-2">
              <v-icon left small color="primary">mdi-scale-balance</v-icon>
              แยกตามเกณฑ์การประเมิน (เฉพาะที่รับรายงานตัวแล้ว)
            </div>
            <v-simple-table dense>
              <tbody>
                <tr>
                  <td>เกณฑ์ใหม่ ว19/2568</td>
                  <td class="text-right font-weight-bold">{{ formatNum(criteriaCount('NEW')) }}</td>
                </tr>
                <tr>
                  <td>เกณฑ์เดิม</td>
                  <td class="text-right font-weight-bold">{{ formatNum(criteriaCount('OLD')) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card outlined rounded="lg" class="pa-3">
            <div class="font-weight-bold mb-2">
              <v-icon left small color="primary">mdi-domain</v-icon>
              สถานศึกษาที่มีครูผู้ช่วยมากที่สุด (รับรายงานตัวแล้ว)
            </div>
            <v-simple-table dense>
              <tbody>
                <tr v-for="c in (summary.by_college || []).slice(0, 5)" :key="c.mt_college_code">
                  <td>{{ c.college_name || c.mt_college_code }}</td>
                  <td class="text-right font-weight-bold">{{ formatNum(c.cnt) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- ===== ความผิดปกติ ===== -->
      <v-card outlined rounded="lg" class="mb-6">
        <v-tabs v-model="anomalyTab" background-color="transparent" color="error">
          <v-tab>
            เลขบัตรซ้ำหลายแถว
            <v-chip x-small color="error" text-color="white" class="ml-2" v-if="anomalies.counts">
              {{ anomalies.counts.duplicate_id_card }}
            </v-chip>
          </v-tab>
          <v-tab>
            รายงานตัวแล้วแต่ไม่มีสถานศึกษา
            <v-chip x-small color="error" text-color="white" class="ml-2" v-if="anomalies.counts">
              {{ anomalies.counts.receipt_without_college }}
            </v-chip>
          </v-tab>
          <v-tab>
            ครบกำหนดประเมินแต่ยังไม่รายงานผล
            <v-chip x-small color="error" text-color="white" class="ml-2" v-if="anomalies.counts">
              {{ anomalies.counts.overdue_evaluation }}
            </v-chip>
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="anomalyTab">
          <!-- เลขบัตรซ้ำ -->
          <v-tab-item>
            <v-card-text>
              <v-alert text color="info" dense class="mb-3">
                เลขบัตรเดียวกันถูกเรียกรายงานตัว/สอบมากกว่า 1 รอบ ตรวจดูว่ารอบใดเป็นรายการจริง รอบใดเป็นรายการค้าง/ไม่สมบูรณ์
              </v-alert>
              <div v-if="!anomalies.duplicate_id_card || anomalies.duplicate_id_card.length === 0" class="text-center grey--text pa-6">
                ไม่พบเลขบัตรซ้ำ
              </div>
              <v-expansion-panels v-else accordion>
                <v-expansion-panel v-for="group in anomalies.duplicate_id_card" :key="group.id_card">
                  <v-expansion-panel-header>
                    <span>
                      {{ group.rows[0].full_name }}
                      <span class="grey--text ml-2">{{ group.id_card }}</span>
                      <v-chip x-small color="warning" class="ml-2">{{ group.rows.length }} แถว</v-chip>
                    </span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th>mt_id</th>
                          <th>รอบที่/ปี</th>
                          <th>สถานศึกษา</th>
                          <th>สถานะรายงานตัว</th>
                          <th>คำสั่งบรรจุ</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in group.rows" :key="row.mt_id">
                          <td>{{ row.mt_id }}</td>
                          <td>{{ row.mt_times }}/{{ row.mt_years }}</td>
                          <td>{{ row.college_name || '-' }}</td>
                          <td>
                            <v-chip x-small :color="statusColor(row.mt_admissions_status)" dark>
                              {{ statusLabel(row.mt_admissions_status) }}
                            </v-chip>
                          </td>
                          <td>{{ row.mt_order_app || '-' }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-tab-item>

          <!-- รับรายงานตัวแต่ไม่มีสถานศึกษา -->
          <v-tab-item>
            <v-card-text>
              <div v-if="!anomalies.receipt_without_college || anomalies.receipt_without_college.length === 0" class="text-center grey--text pa-6">
                ไม่พบรายการผิดปกติ
              </div>
              <v-simple-table dense v-else>
                <thead>
                  <tr>
                    <th>ชื่อ-สกุล</th>
                    <th>เลขบัตร</th>
                    <th>รอบที่/ปี</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in anomalies.receipt_without_college" :key="row.mt_id">
                    <td>{{ row.full_name }}</td>
                    <td>{{ row.mt_id_card }}</td>
                    <td>{{ row.mt_times }}/{{ row.mt_years }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-tab-item>

          <!-- ครบกำหนดแต่ไม่รายงานผล -->
          <v-tab-item>
            <v-card-text>
              <v-alert text color="warning" dense class="mb-3">
                คำนวณจากวันบรรจุ + 6/12/18/24 เดือน เทียบกับผลการประเมินที่มีอยู่ในระบบ
              </v-alert>
              <div v-if="!anomalies.overdue_evaluation || anomalies.overdue_evaluation.length === 0" class="text-center grey--text pa-6">
                ไม่พบรายการค้างประเมิน
              </div>
              <v-simple-table dense v-else>
                <thead>
                  <tr>
                    <th>ชื่อ-สกุล</th>
                    <th>สถานศึกษา</th>
                    <th>วันบรรจุ</th>
                    <th>รอบที่ค้าง</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in anomalies.overdue_evaluation" :key="row.mt_id_card">
                    <td>{{ row.full_name }}</td>
                    <td>{{ row.college_name || row.mt_college_code }}</td>
                    <td>{{ row.mt_appoin_day }}/{{ row.mt_appoin_month }}/{{ row.mt_appoin_year }}</td>
                    <td>
                      <v-chip x-small color="error" dark class="mr-1" v-for="rnd in row.missing_rounds" :key="rnd">
                        ครั้งที่ {{ rnd }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <!-- ===== ส่งออก Excel ===== -->
      <v-card outlined rounded="lg" class="pa-4">
        <div class="d-flex align-center justify-space-between flex-wrap">
          <div>
            <div class="font-weight-bold">ส่งออกข้อมูลครูผู้ช่วยทั้งหมด (Excel)</div>
            <div class="caption grey--text">เฉพาะรายการที่รับรายงานตัวเรียบร้อยและมีสถานศึกษา</div>
          </div>
          <v-btn color="success" :loading="exporting" @click="exportExcel">
            <v-icon left>mdi-file-excel</v-icon>
            ส่งออก Excel
          </v-btn>
        </div>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import assistant_teacher_bar from "../../components/admin/assistant_teacher_bar.vue";

export default {
  name: "AssistantTeacherMonitor",
  components: { assistant_teacher_bar },
  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      exporting: false,
      anomalyTab: 0,
      summary: {},
      anomalies: {}
    };
  },
  async mounted() {
    await this.loadAll();
  },
  methods: {
    async loadAll() {
      this.loading = true;
      try {
        const [summaryRes, anomaliesRes] = await Promise.all([
          this.$http.post("assistant_teacher_monitor.php", { ApiKey: this.ApiKey, action: "summary" }),
          this.$http.post("assistant_teacher_monitor.php", { ApiKey: this.ApiKey, action: "anomalies" })
        ]);
        this.summary = summaryRes.data && summaryRes.data.status ? summaryRes.data : {};
        this.anomalies = anomaliesRes.data && anomaliesRes.data.status ? anomaliesRes.data : {};
      } finally {
        this.loading = false;
      }
    },

    async exportExcel() {
      this.exporting = true;
      try {
        const result = await this.$http.post("assistant_teacher_monitor.php", { ApiKey: this.ApiKey, action: "export" });
        const rows = (result.data && result.data.status ? result.data.data : []) || [];
        const data = rows.map(r => ({
          เลขบัตรประชาชน: r.mt_id_card,
          "ชื่อ-สกุล": r.full_name,
          สถานศึกษา: r.college_name || r.mt_college_code || "-",
          จังหวัด: r.province_name || "-",
          ภาค: r.region_name || "-",
          เลขที่ตำแหน่ง: r.mt_id_position || "-",
          คำสั่งบรรจุ: r.mt_order_app || "-",
          วันบรรจุ: [r.mt_appoin_day, r.mt_appoin_month, r.mt_appoin_year].filter(Boolean).join("/"),
          เกณฑ์การประเมิน: r.criteria_version,
          จำนวนรอบที่ประเมินแล้ว: r.evaluation_count || 0,
          รอบล่าสุดที่ประเมิน: r.latest_round || "-"
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, ws, "ครูผู้ช่วย");
        XLSX.writeFile(wb, `ข้อมูลครูผู้ช่วย_${new Date().toISOString().slice(0, 10)}.xlsx`);
      } finally {
        this.exporting = false;
      }
    },

    statusCount(key) {
      const list = this.summary.by_admissions_status;
      if (!list) return 0;
      return parseInt(list[key] || 0);
    },

    criteriaCount(key) {
      const list = this.summary.by_criteria_version;
      if (!list) return 0;
      return parseInt(list[key] || 0);
    },

    statusLabel(status) {
      return (
        {
          receipt: "มารายงานตัว",
          missing: "ไม่มารายงานตัว",
          send: "ส่งคำร้องแล้ว",
          pending: "รอดำเนินการ"
        }[status] || status || "ไม่ระบุ"
      );
    },

    statusColor(status) {
      return (
        {
          receipt: "success",
          missing: "error",
          send: "info",
          pending: "grey"
        }[status] || "grey"
      );
    },

    formatNum(n) {
      return new Intl.NumberFormat("th-TH").format(n || 0);
    }
  }
};
</script>
